import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { TextField, SelectField } from "@/components/common/Field";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { BrandLogo } from "@/components/common/BrandIdentity";
import { CheckCircle2 } from "lucide-react";

const ROLES = ["Advocate", "Junior Advocate", "Senior Advocate", "Firm Partner", "Law Firm Administrator", "Clerk", "In-house Counsel", "Client", "ODR Neutral", "Mediator", "Arbitrator", "Other"];
const STEPS = ["Personal", "Role", "Practice", "Workspace"];

export default function Signup() {
  const { register } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({ role: "Advocate", teamSize: "1-5" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const finish = async () => {
    if (!f.email || !f.password || !f.fullName) { toast.error("Name, email and password are required."); setStep(0); return; }
    try {
      const result = await register({ name: f.fullName, email: f.email, password: f.password, org_name: f.org || f.workspace });
      if (result?.mfaRequired) { navigate("/login"); return; }
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create your account.");
      setStep(0);
    }
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-6">
        <div className="max-w-md text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-success" />
          <h1 className="mt-5 font-display text-4xl font-semibold text-ink">Your legal workspace is ready.</h1>
          <p className="mt-2 text-ink-2">Welcome aboard, {f.fullName?.split(" ")[0] || "Counsel"}. Everything is set up for you.</p>
          <Button className="mt-6" size="lg" onClick={() => navigate("/dashboard")} data-testid="enter-app-btn">Enter Nyayyaksha</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 py-10">
      <div className="w-full max-w-xl">
        <div className="mb-6 flex items-center gap-3">
          <BrandLogo className="h-14 w-14" testId="signup-brand-logo" />
          <div className="font-display text-2xl font-semibold text-ink">NYAYYAKSHA</div>
        </div>

        <div className="mb-6 flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-1 items-center gap-1.5">
              <div className={cn("flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold", i <= step ? "bg-copper text-white" : "bg-secondary text-ink-2")}>{i + 1}</div>
              <span className={cn("text-xs", i === step ? "font-medium text-ink" : "text-ink-muted")}>{s}</span>
              {i < STEPS.length - 1 && <div className={cn("h-px flex-1", i < step ? "bg-copper" : "bg-line")} />}
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-line bg-card p-6">
          {step === 0 && (
            <div className="space-y-3">
              <h2 className="font-display text-2xl font-semibold text-ink">Personal details</h2>
              <TextField label="Full Name" value={f.fullName || ""} onChange={(v) => set("fullName", v)} testid="signup-name" />
              <TextField label="Email" value={f.email || ""} onChange={(v) => set("email", v)} testid="signup-email" />
              <TextField label="Mobile" value={f.mobile || ""} onChange={(v) => set("mobile", v)} />
              <TextField label="Password" type="password" value={f.password || ""} onChange={(v) => set("password", v)} />
              <div className="relative py-1"><div className="absolute inset-0 flex items-center"><span className="w-full border-t border-line" /></div><div className="relative flex justify-center"><span className="bg-card px-2 text-xs text-ink-muted">or</span></div></div>
              <Button type="button" variant="outline" className="w-full" onClick={googleLogin} data-testid="signup-google">Sign up with Google</Button>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-3">
              <h2 className="font-display text-2xl font-semibold text-ink">Professional role</h2>
              <SelectField label="Select your role" value={f.role} onChange={(v) => set("role", v)} options={ROLES} testid="signup-role" />
            </div>
          )}
          {step === 2 && (
            <div className="grid grid-cols-2 gap-3">
              <h2 className="col-span-2 font-display text-2xl font-semibold text-ink">Practice profile</h2>
              <TextField label="State" value={f.state || ""} onChange={(v) => set("state", v)} />
              <TextField label="Primary Court" value={f.court || ""} onChange={(v) => set("court", v)} />
              <TextField label="Practice Areas" value={f.areas || ""} onChange={(v) => set("areas", v)} />
              <TextField label="Years of Practice" type="number" value={f.years || ""} onChange={(v) => set("years", v)} />
              <TextField label="Bar Council Enrolment No." value={f.enrolment || ""} onChange={(v) => set("enrolment", v)} />
              <TextField label="Organisation / Firm" value={f.org || ""} onChange={(v) => set("org", v)} />
            </div>
          )}
          {step === 3 && (
            <div className="space-y-3">
              <h2 className="font-display text-2xl font-semibold text-ink">Workspace setup</h2>
              <TextField label="Workspace Name" value={f.workspace || ""} onChange={(v) => set("workspace", v)} testid="signup-workspace" />
              <SelectField label="Team Size" value={f.teamSize} onChange={(v) => set("teamSize", v)} options={["1-5", "6-15", "16-50", "50+"]} />
              <TextField label="Primary Practice Location" value={f.location || ""} onChange={(v) => set("location", v)} />
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" onClick={step === 0 ? () => navigate("/login") : () => setStep((s) => s - 1)}>
              {step === 0 ? "Back to sign in" : "Back"}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep((s) => s + 1)} data-testid="signup-next">Continue</Button>
            ) : (
              <Button onClick={finish} data-testid="signup-finish">Complete Setup</Button>
            )}
          </div>
        </div>

        <p className="mt-5 text-center text-sm text-ink-2">
          Already have an account? <Link to="/login" className="font-medium text-copper hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
